import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Router from 'next/router';
import { useTranslations, useLocale } from 'next-intl';
import { parse } from 'url';

import {
  filterPopValue,
  filterPushValue,
  filterInit,
  filterFetchData,
  filterResetValues
} from '../../../redux/actions/filter';
import { buildFilterQuery } from '../../../helper/api';

import CloseIcon from '../../icons/close/close';
import StyledFilter from './filter.style';

export const Filter = ({
  updateFilterValue,
  hasMargin = false,
  tags,
  time,
  insideHeader = false,
  isLoading,
  filteredValues,
  initFilterValues,
  resetFilterValues,
  ...props
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const customHistory = typeof window !== 'undefined' ? [window.location.href] : null;

  const handleRouteChange = filters => {
    const { page } = Router.query;

    const options = page && parseInt(page, 10) > 1 ? {} : { shallow: true };
    const slug = time === 'was-ist' || time === 'what-is' ? 'index' : time;

    // Page should always be 1 to avoid filtering on higher pages resulting in no results.
    const filterQuery = buildFilterQuery(filters, 1);

    Router.replace(
      {
        pathname: '/time',
        query: { filters, page: 1, slug, lang: locale }
      },
      `/${locale}/${slug !== 'index' ? slug : ''}${filterQuery}`,
      options
    );
  };

  const updateFilter = event => {
    const { page } = Router.query;
    const { value, checked } = event.target;

    updateFilterValue(value, checked, parseInt(page, 10) === 1);

    const newFilterArray = checked
      ? [value, ...filteredValues]
      : filteredValues.filter(item => item !== value);

    handleRouteChange(newFilterArray);
  };

  const resetFilter = () => {
    if (parse(window.location.href).pathname !== parse(customHistory[0]).pathname) {
      resetFilterValues();
    }
    customHistory.unshift(window.location.href);
  };

  useEffect(() => {
    const initialFilterValues = tags.filter(tag => tag.active === true).map(tag => tag.slug);

    if (initialFilterValues && initialFilterValues.length > 0) {
      initFilterValues(initialFilterValues);
    }
    return () => {
      resetFilter();
    };
  }, []);

  return (
    <StyledFilter
      insideHeader={insideHeader}
      isLoading={isLoading}
      hasMargin={hasMargin}
      {...props}
    >
      <div className="filter__wrapper">
        <div id="filter-label" className="filter__label">
          {insideHeader ? <span>Filter</span> : t('filter.filterContent')}
        </div>
        <div className="filter__list" role="group" aria-labelledby="filter-label">
          <div className="filter__list-wrapper">
            {tags.map(tag => (
              <div className="filter__item" key={tag.slug}>
                <input
                  type="checkbox"
                  id={tag.slug}
                  name="filter"
                  value={tag.slug}
                  onChange={e => updateFilter(e)}
                  checked={filteredValues.indexOf(tag.slug) === -1 ? '' : true}
                  aria-checked={filteredValues.indexOf(tag.slug) === -1 ? 'false' : 'true'}
                  disabled={isLoading}
                  aria-disabled={isLoading}
                />
                <label htmlFor={tag.slug} className="filter__name">
                  {tag.title}
                  {filteredValues.indexOf(tag.slug) !== -1 && <CloseIcon />}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StyledFilter>
  );
};

Filter.propTypes = {
  hasMargin: PropTypes.bool,
  time: PropTypes.string.isRequired,
  insideHeader: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  initFilterValues: PropTypes.func.isRequired,
  resetFilterValues: PropTypes.func.isRequired,
  updateFilterValue: PropTypes.func.isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  filteredValues: PropTypes.arrayOf(PropTypes.string)
};

const mapDispatchToProps = dispatch => ({
  updateFilterValue: (value, checked, fetchDynamically) => {
    if (checked) {
      dispatch(filterPushValue(value));
    } else {
      dispatch(filterPopValue(value));
    }
    if (fetchDynamically) {
      dispatch(filterFetchData());
    }
  },
  resetFilterValues: () => {
    dispatch(filterResetValues());
  },
  initFilterValues: value => {
    dispatch(filterInit(value));
  }
});

const mapStateToProps = state => {
  const { filter, app } = state;
  return {
    time: app.pageData.time,
    tags: app.pageData.tags,
    isLoading: filter.isLoading,
    filteredValues: filter.filteredValues
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Filter);
